import { isStopDistance } from "@auto-pools/core";
import { useEffect, useState } from "react";
import { formatStopDistance, INVALID_STOP_DISTANCE_REASON } from "../lib/stopPolicy";
import type { Token } from "../types";

/** `THRESHOLDS` (5/10/15/20/25) with a real, honoured zero prepended. */
const STOP_PRESETS = [0, 5, 10, 15, 20, 25] as const;

export interface StopDistanceRowView {
  /** Formatted trigger price; undefined while no preview is available yet. */
  price: string | undefined;
  /** Formatted USD value of the trigger; null when unpriced (see `usdReason`). */
  usd: string | null;
  /** Formatted, signed "from the current price" text; null when the current price is unavailable. */
  fromCurrent: string | null;
}

export interface StopDistanceControlProps {
  below: string;
  above: string;
  onChange: (next: { below: string; above: string }) => void;
  mirror: boolean;
  onMirror: (value: boolean) => void;
  rows: { below: StopDistanceRowView; above: StopDistanceRowView };
  unit: string;
  /** Shown once, next to a null `usd`, when the quote token has no USD price. */
  usdReason?: string;
  /** Fires whenever a row's Custom text is invalid or clean, so a caller can gate its submit. */
  onValidityChange?: (invalid: boolean) => void;
  tokens: readonly [Token, Token];
  targetMint: string;
  onTarget: (mint: string) => void;
  preferredMint?: string | null;
  /** A one-line note under the control, e.g. a legacy one-sided or disabled prior policy. */
  notice?: string | null;
  geometryError?: string;
}

function matchesPreset(value: string): boolean {
  const numeric = Number(value);
  return STOP_PRESETS.some((preset) => preset === numeric);
}

interface RowProps {
  side: "below" | "above";
  label: string;
  arrow: string;
  value: string;
  view: StopDistanceRowView;
  usdReason?: string;
  unit: string;
  zeroLabel: string;
  draft: string;
  custom: boolean;
  reason?: string;
  onPreset: (preset: number) => void;
  onDraft: (text: string) => void;
  onBlur: () => void;
}

function StopRow(p: RowProps) {
  const zero = Number(p.value) === 0;
  return (
    <div
      className={`stop-distance-row stop-distance-${p.side}`}
      role="group"
      aria-label={`${p.label} distance`}
    >
      <div className="stop-distance-readout">
        <span className="stop-distance-label">
          {p.arrow} {p.label}
        </span>
        <span className="stop-distance-value num">{formatStopDistance(p.value)}</span>
        <span className="stop-distance-price num" title={`Trigger price in ${p.unit}`}>
          {p.view.price ?? "—"}
        </span>
        <span className="stop-distance-usd num">
          {p.view.usd ?? "—"}
          {p.view.usd === null && p.usdReason && <small>{p.usdReason}</small>}
        </span>
      </div>
      {zero ? (
        <small className="stop-distance-subline warn">{p.zeroLabel}</small>
      ) : (
        <small className="stop-distance-subline">
          {p.view.fromCurrent ? `${p.view.fromCurrent} from the current price` : "—"}
        </small>
      )}
      <div className="stop-distance-controls-row">
        <div className="choice-grid six stop-distance-presets">
          {STOP_PRESETS.map((preset) => (
            <button
              type="button"
              key={preset}
              aria-pressed={!p.custom && Number(p.value) === preset}
              className={!p.custom && Number(p.value) === preset ? "selected" : ""}
              onClick={() => {
                p.onPreset(preset);
              }}
            >
              {preset}%
            </button>
          ))}
        </div>
        <div className="stop-distance-custom-field mono">
          <input
            className="stop-distance-custom"
            inputMode="decimal"
            placeholder="Custom"
            aria-label={`Custom distance ${p.side} range`}
            aria-invalid={p.reason !== undefined}
            value={p.draft}
            onChange={(event) => {
              p.onDraft(event.target.value);
            }}
            onBlur={p.onBlur}
          />
          <span className="stop-distance-custom-suffix" aria-hidden="true">
            %
          </span>
        </div>
      </div>
      {p.reason && (
        <p className="inline-alert error stop-distance-reason" role="alert">
          {p.reason}
        </p>
      )}
    </div>
  );
}

export function StopDistanceControl(p: StopDistanceControlProps) {
  const [belowCustom, setBelowCustom] = useState(() => !matchesPreset(p.below));
  const [aboveCustom, setAboveCustom] = useState(() => !matchesPreset(p.above));
  const [belowDraft, setBelowDraft] = useState(() => (belowCustom ? p.below : ""));
  const [aboveDraft, setAboveDraft] = useState(() => (aboveCustom ? p.above : ""));
  const [belowReason, setBelowReason] = useState<string | undefined>();
  const [aboveReason, setAboveReason] = useState<string | undefined>();

  useEffect(() => {
    p.onValidityChange?.(belowReason !== undefined || aboveReason !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notifies on the reason pair only.
  }, [belowReason, aboveReason]);

  function commit(next: { below?: string; above?: string }, custom: boolean) {
    const below = next.below ?? p.below;
    const above = next.above ?? p.above;
    if (p.mirror) {
      const value = next.below ?? next.above ?? below;
      p.onChange({ below: value, above: value });
      setBelowCustom(custom);
      setAboveCustom(custom);
      setBelowDraft(custom ? value : "");
      setAboveDraft(custom ? value : "");
      setBelowReason(undefined);
      setAboveReason(undefined);
      return;
    }
    p.onChange({ below, above });
    if (next.below !== undefined) {
      setBelowCustom(custom);
      setBelowDraft(custom ? next.below : "");
      setBelowReason(undefined);
    }
    if (next.above !== undefined) {
      setAboveCustom(custom);
      setAboveDraft(custom ? next.above : "");
      setAboveReason(undefined);
    }
  }

  function pressPreset(side: "below" | "above", preset: number) {
    commit({ [side]: String(preset) }, false);
  }

  function editDraft(side: "below" | "above", text: string) {
    if (side === "below") setBelowDraft(text);
    else setAboveDraft(text);
    if (isStopDistance(text)) {
      commit({ [side]: text }, true);
      return;
    }
    if (side === "below") setBelowReason(INVALID_STOP_DISTANCE_REASON);
    else setAboveReason(INVALID_STOP_DISTANCE_REASON);
  }

  function blurDraft(side: "below" | "above") {
    const draft = side === "below" ? belowDraft : aboveDraft;
    const committed = side === "below" ? p.below : p.above;
    // Only an empty field falls back. A non-empty invalid draft ("60" after "6" was committed on
    // the way) stays visible with its reason, so the caller's submit stays blocked instead of
    // silently saving a value the operator never meant.
    if (draft !== "") return;
    if (side === "below") {
      setBelowDraft(belowCustom ? committed : "");
      setBelowReason(undefined);
    } else {
      setAboveDraft(aboveCustom ? committed : "");
      setAboveReason(undefined);
    }
  }

  const [tokenA, tokenB] = p.tokens;

  return (
    <div className="stop-distance-control">
      <div className="stop-distance-header">
        <label className="toggle stop-distance-mirror">
          <input
            type="checkbox"
            role="switch"
            checked={p.mirror}
            onChange={(event) => {
              p.onMirror(event.target.checked);
            }}
          />
          <span />
          <small>Same both sides</small>
        </label>
      </div>
      <StopRow
        side="below"
        label="Below range"
        arrow="↓"
        value={p.below}
        view={p.rows.below}
        usdReason={p.usdReason}
        unit={p.unit}
        zeroLabel="at the range start"
        draft={belowDraft}
        custom={belowCustom}
        reason={belowReason}
        onPreset={(preset) => {
          pressPreset("below", preset);
        }}
        onDraft={(text) => {
          editDraft("below", text);
        }}
        onBlur={() => {
          blurDraft("below");
        }}
      />
      <StopRow
        side="above"
        label="Above range"
        arrow="↑"
        value={p.above}
        view={p.rows.above}
        usdReason={p.usdReason}
        unit={p.unit}
        zeroLabel="at the range end"
        draft={aboveDraft}
        custom={aboveCustom}
        reason={aboveReason}
        onPreset={(preset) => {
          pressPreset("above", preset);
        }}
        onDraft={(text) => {
          editDraft("above", text);
        }}
        onBlur={() => {
          blurDraft("above");
        }}
      />
      <p className="hint stop-distance-notice">
        Negative moves inside the LP range; zero sits on its edge; positive moves outside. Custom
        accepts −50% to 50% with at most one decimal.
      </p>
      {p.geometryError && (
        <p className="inline-alert error" role="alert">
          {p.geometryError}
        </p>
      )}
      {p.notice && <p className="hint stop-distance-notice">{p.notice}</p>}
      <fieldset className="stop-distance-target">
        <legend>Return as</legend>
        <div className="choice-grid halves">
          {[tokenA, tokenB].map((token) => (
            <button
              type="button"
              key={token.mint}
              aria-pressed={p.targetMint === token.mint}
              className={p.targetMint === token.mint ? "selected" : ""}
              onClick={() => {
                p.onTarget(token.mint);
              }}
            >
              {token.symbol}
              {p.preferredMint === token.mint && <small>preferred</small>}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
